import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useServerView, activeFilters, ListActions, ListPrint, clamp2 } from "@/components/list/ListView";
import { QrFilterBar } from "@/components/quality/QrFilterBar";
import { PSummary } from "@/components/print/PrintDoc";
import { pl } from "@/components/quality/QrTools";
import { ArchiveButton } from "@/components/ArchiveDialog";
import { daDate } from "@/lib/date";
import { Button } from "@/components/ui/button";
import { History, FileText, Loader2, RotateCcw, FileSpreadsheet, Share2, Trash2 } from "lucide-react";


function Card({ children, className = "" }) { return <div className={`bg-white rounded-2xl border border-[#EBE8E3] ${className}`}>{children}</div>; }

export default function AuditHistory() {
  const nav = useNavigate();
  const sv = useServerView("/audit-view/history", { exportPath: "/audit-view/history/export.xlsx" });
  const audits = sv.rows;
  const loading = sv.data === null;
  const load = () => sv.reload();
  const year = sv.getArr("aar")[0] || ""; const areaId = sv.getArr("omraade")[0] || "";
  const filters = activeFilters(sv, { aar: "År", omraade: "Område", auditor: "Auditor" });
  const reopen = async (aid) => {
    if (!window.confirm("Genåbn denne audit? Status sættes til 'I gang'.")) return;
    try { await api.post(`/audit/audits/${aid}/reopen`); toast.success("Audit genåbnet"); nav(`/kvalitet/audit/${aid}/udfoer`); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const delAudit = async (aid) => {
    if (!window.confirm("Arkivér denne audit inkl. tilhørende fund? Den kan gendannes fra arkivet.")) return;
    try { await api.delete(`/audit/audits/${aid}`); toast.success("Audit arkiveret"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const download = async (url, filename) => {
    try {
      const res = await api.get(url, { responseType: "blob" });
      const u = URL.createObjectURL(res.data);
      const l = document.createElement("a"); l.href = u; l.download = filename;
      document.body.appendChild(l); l.click(); l.remove(); URL.revokeObjectURL(u);
    } catch (e) { toast.error("Kunne ikke hente filen"); }
  };
  const exportExcel = () => {
    const p = new URLSearchParams({ status: "Afsluttet" });
    if (year) p.set("year", year);
    if (areaId) p.set("area_id", areaId);
    download(`/audit/audits/export/excel?${p.toString()}`, "audithistorik.xlsx");
  };

  return (
    <div className="space-y-6" data-testid="audit-history-page">
      <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4">
        <div>
          <p className="label-caps flex items-center gap-2 text-[#806B61]"><History className="h-3.5 w-3.5" /> Kvalitet · Intern audit</p>
          <h1 className="text-4xl font-semibold text-[#1A1919] mt-1">Audithistorik</h1>
          <p className="text-stone-500 mt-2">Afsluttede audits med rapport og fund.</p>
        </div>
        <div className="flex items-center gap-2 shrink-0"><ListActions onExport={sv.exportXlsx} more={[]}><ArchiveButton kind="audits" onRestored={load} /></ListActions></div>
      </div>

      <QrFilterBar search={sv.search} setSearch={sv.onSearch} hideQuick hideSaved placeholder="Søg auditnr., titel, område, auditor…" shown={audits.length} total={sv.data?.total ?? 0} onReset={sv.reset} match={sv.match} setMatch={sv.setMatch}
        filters={[sv.mkFilter("aar", "År"), sv.mkFilter("omraade", "Område"), sv.mkFilter("auditor", "Auditor")]} period={sv.period} />
      <ListPrint kicker="BSM A/S · Intern audit" title="Audithistorik" noun="audits" filters={filters}
        kpis={[{ label: "Audits i udvalget", value: audits.length }, { label: "Åbne fund", value: sv.data?.counts?.open || 0, dark: true }, { label: "Fund i alt", value: sv.data?.counts?.findings || 0 }, { label: "Områder", value: new Set(audits.flatMap((a) => a.area_ids || [])).size }]}
        summary={<PSummary>{pl(audits.length, "afsluttet audit", "afsluttede audits")} i udvalget med {pl(sv.data?.counts?.findings || 0, "fund", "fund")}, heraf {sv.data?.counts?.open || 0} fortsat åbne.</PSummary>}
        cols={[{ label: "Auditnr.", key: "audit_no", w: "24mm", cls: "nowrap" }, { label: "Dato", key: "dato", w: "22mm", cls: "nowrap" }, { label: "Titel", key: "t2" }, { label: "Områder", key: "o2", w: "40mm" }, { label: "Auditor", key: "au2", w: "30mm" }, { label: "Fund", key: "fc", w: "14mm", cls: "nowrap" }]}
        rows={audits.map((a) => ({ ...a, dato: daDate(a.actual_date || a.planned_date), t2: clamp2(a.title), o2: clamp2(a._areas), au2: clamp2(a.auditor_name), fc: Object.entries(a.counts || {}).filter(([k]) => k !== "open").reduce((n, [, v]) => n + (v || 0), 0) }))} />

      {loading ? <div className="flex items-center gap-2 text-stone-400"><Loader2 className="h-4 w-4 animate-spin" /> Indlæser…</div> : audits.length === 0 ? (
        <Card className="p-8 text-center text-stone-400">Ingen afsluttede audits fundet.</Card>
      ) : (
        <div className="pcard overflow-hidden" data-testid="hist-list">
          <div className="hidden md:grid grid-cols-12 gap-4 px-5 py-3 bg-[#FBFAF8] border-b border-[#EBE8E3]">
            <span className="col-span-2 label-caps">Audit</span>
            <span className="col-span-4 label-caps">Titel &amp; områder</span>
            <span className="col-span-2 label-caps">Auditor</span>
            <span className="col-span-2 label-caps">Fund</span>
            <span className="col-span-2 label-caps text-right">Handling</span>
          </div>
          <div className="divide-y divide-[#F0EDE8]">
          {audits.map((a) => (
            <div key={a.id} className="grid grid-cols-1 md:grid-cols-12 gap-2 md:gap-4 md:items-center px-5 py-4 hover:bg-[#FBFAF8] [transition-property:background-color] duration-150 cursor-pointer" data-testid={`hist-audit-${a.id}`} onClick={() => nav(`/kvalitet/audit/${a.id}/rapport`)}>
              <div className="md:col-span-2 min-w-0">
                <span className="font-mono text-xs text-stone-500">{a.audit_no}</span>
                <p className="text-xs text-stone-400 mt-0.5">{daDate(a.actual_date)}</p>
              </div>
              <div className="md:col-span-4 min-w-0">
                <p className="font-semibold text-[#1A1919] md:truncate">{a.title}</p>
                <p className="text-xs text-stone-500 mt-0.5 md:truncate">Områder: {(a.area_reviews || []).map((r) => r.area_name).join(", ") || "—"}</p>
              </div>
              <div className="md:col-span-2 text-sm text-stone-600 truncate"><span className="md:hidden label-caps mr-2">Auditor</span>{a.auditor_name || "—"}</div>
              <div className="md:col-span-2 flex flex-wrap gap-1.5 text-[11px]">
                <span className="px-2 py-0.5 rounded-full bg-[#F1DEDA] text-[#A83232] border border-[#E9C9C3]">{a.counts?.Afvigelse || 0} afv.</span>
                <span className="px-2 py-0.5 rounded-full bg-[#F4EFEC] text-[#806B61] border border-[#E6DDD6]">{a.counts?.Observation || 0} obs.</span>
                <span className="px-2 py-0.5 rounded-full bg-[#E7EADF] text-[#5f6b4a] border border-[#D3D8C6]">{a.counts?.Forslag || 0} forslag</span>
                {(a.counts?.open || 0) > 0 && <span className="px-2 py-0.5 rounded-full bg-white text-[#A83232] border border-[#E9C9C3] font-medium">{a.counts.open} åbne</span>}
              </div>
              <div className="md:col-span-2 flex items-center gap-1 md:justify-end flex-wrap" onClick={(e) => e.stopPropagation()}>
                <button type="button" data-testid={`hist-open-${a.id}`} onClick={(e) => { e.stopPropagation(); nav(`/kvalitet/audit/${a.id}/rapport`); }} title="Se rapport og fund" className="p-1.5 rounded-lg text-[#806B61] hover:bg-[#F3F1EC]"><FileText className="h-4 w-4" /></button>
                <button type="button" data-testid={`hist-share-${a.id}`} onClick={(e) => { e.stopPropagation(); nav(`/kvalitet/audit/${a.id}/rapport`, { state: { openShare: true } }); }} title="Del" className="p-1.5 rounded-lg text-stone-600 hover:bg-[#F3F1EC]"><Share2 className="h-4 w-4" /></button>
                <button type="button" data-testid={`hist-reopen-${a.id}`} onClick={(e) => { e.stopPropagation(); reopen(a.id); }} title="Genåbn" className="p-1.5 rounded-lg text-[#806B61] hover:bg-[#F3F1EC]"><RotateCcw className="h-4 w-4" /></button>
                <button type="button" data-testid={`hist-delete-${a.id}`} onClick={(e) => { e.stopPropagation(); delAudit(a.id); }} title="Arkivér" className="p-1.5 rounded-lg text-[#A83232] hover:bg-[#F1DEDA]"><Trash2 className="h-4 w-4" /></button>
              </div>
            </div>
          ))}
          </div>
        </div>
      )}
    </div>
  );
}
